/**
 * Camlin Companion — live contextual-chat session wiring (doc 116 §2.3/§7 D2, CO1).
 *
 * Pure config-builder functions, kept side-effect free on purpose: this
 * project's test suite runs on `tsx --test` (no jsdom / DOM renderer), so
 * anything that needs unit coverage has to be testable without mounting
 * `<AvatarEmbed>`'s Three.js tree. `CompanionWidget.tsx` calls these to
 * build the config it hands to `<AvatarEmbed>`; the widget itself stays a
 * thin, mostly-untested wiring layer.
 */

import type { AvatarEmbedConfig } from '../sdk/AvatarEmbed';
import type { AvatarHostLiveConfig } from '../host/adapter';
import type { CompanionPageContext, CompanionSiteConfig } from './types';

/** `live.contextualChat.host` value — routes per host-contextual-chat.mdc. */
export const COMPANION_CONTEXTUAL_CHAT_HOST = 'companion';

export interface CompanionLiveSessionOptions {
  /** Page-scoped visitor session id, when the host page already minted one. */
  sessionId?: string;
  /**
   * CO2 (doc 116 §2.4/§7 D3) — stable per-browser visitor id
   * (`visitorId.ts`), used server-side only for engagement-cap/dismiss and
   * transcript-retention bookkeeping. Rides `contextualChat.metadata`'s RAW
   * passthrough (`buildContextualChatMetadata`'s `Object.assign` in
   * liveMode.ts — unlike the later live-update path, initial session
   * metadata is NOT allowlist-filtered), so it needs no new wire format.
   */
  visitorId?: string;
  /**
   * CO2 (doc 116 §2.1.2) — the bridge's initial page-context digest,
   * captured before the session opens. SPA-navigation follow-up digests do
   * NOT go through this config — they use the `avatar:live:contextUpdate`
   * window event (see `CompanionWidget.tsx`), which forwards its detail to
   * `LiveModeClient.sendSessionContext` unfiltered; `buildCompanionLiveConfig`
   * only covers the FIRST capture, delivered at session-open time.
   */
  pageContext?: CompanionPageContext;
}

/**
 * Builds the `AvatarEmbedConfig.live` contextual-chat seed for a Companion
 * site. Sets `metadata.host = 'companion'` (via `contextualChat.host`) so
 * the routing chain documented in host-contextual-chat.mdc carries it end
 * to end: SDK `buildContextualChatMetadata` → `useLiveSessionContext` →
 * runtime `mergeContextualLiveState` → `fetchContextualLiveReply`.
 */
export function buildCompanionLiveConfig(
  config: CompanionSiteConfig,
  options: CompanionLiveSessionOptions = {}
): AvatarHostLiveConfig {
  return {
    enabled: true,
    liveSessionKind: 'contextual-chat',
    // Companion is chat-first, never voice-first (doc 116 §4) — open the
    // visual channel as soon as the avatar loads instead of waiting for a
    // manual "Listen" click, so the first typed message doesn't stall on a
    // multi-step handshake.
    autoOpenChannel: true,
    // CompanionWidget renders its own minimal transcript/input and drives
    // the session via the AvatarEmbedRef.sendText -> visual.input path, not
    // browser mic input — the SDK must not treat this as a voice host.
    handleInputExternally: true,
    // The SDK's live status overlay ("LISTENING"/"SPEAKING" chips, inline
    // assistant copy) is sized for a full-page live surface; inside the
    // Companion's launcher/face stage it clips into unreadable fragments
    // (Keith's "circle with no text to say what it is", 5 Aug 2026).
    // Companion renders all of its own status UI.
    suppressEmbeddedLiveCopy: true,
    // Session tenant is the REAL platform tenant (runtimeTenantId), never
    // the control-plane slug: the runtime entitlement gate fails closed on
    // unknown tenants (tenant-not-found), and slugs like 'camlin' have no
    // tenant record. Omitting it entirely lets the runtime apply its
    // SESSION_FALLBACK_TENANT_ID — see CompanionSiteConfig.runtimeTenantId.
    sessionContext: {
      ...(config.runtimeTenantId ? { tenantId: config.runtimeTenantId } : {}),
      ...(options.sessionId ? { sessionId: options.sessionId } : {})
    },
    contextualChat: {
      host: COMPANION_CONTEXTUAL_CHAT_HOST,
      assistant: {
        id: config.siteKey,
        name: config.persona.displayName,
        prompt: config.persona.specialistPrompt
      },
      metadata: {
        siteKey: config.siteKey,
        tenantId: config.tenantId,
        siteName: config.siteName,
        ...(options.visitorId ? { visitorId: options.visitorId } : {}),
        ...(options.pageContext ? { pageContext: options.pageContext } : {})
      }
    }
  };
}

/**
 * Full `AvatarEmbedConfig` for mounting the real avatar SDK widget as the
 * Companion's visible face. Native mode only (script-tag embed has no
 * iframe host page of its own to sandbox into) with the SDK's built-in
 * chat/button chrome hidden — Companion owns its own transcript/input UI.
 */
export function buildCompanionAvatarEmbedConfig(
  config: CompanionSiteConfig,
  options: CompanionLiveSessionOptions = {}
): AvatarEmbedConfig {
  return {
    mode: 'native',
    live: buildCompanionLiveConfig(config, options),
    avatarModelId: config.avatar.modelId,
    voiceId: config.avatar.voiceId,
    hideChat: true,
    hideButtons: true,
    // The SDK health pip clips inside the launcher circle; the
    // Companion surfaces connection state through its own status line.
    hideHealthBadge: true,
    // The SDK's orange AudioPermissionChip rendered ON TOP of the face in
    // the minimized launcher and — because the Companion's stage wrapper is
    // pointer-events:none — did nothing when tapped (Keith's screenshot,
    // 6 Aug 2026). The Companion renders its own labelled "Enable sound" /
    // "Allow microphone" buttons inside the open panel instead, fed by
    // AvatarEmbed's onBrowserAccessPromptChange callback.
    hideAudioPermissionChip: true,
    // No cache-warming fetch: it races the GLTF loader's own fetch of the
    // same multi-MB model URL and Chromium fails one of the two concurrent
    // cache writers (net::ERR_CACHE_WRITE_FAILURE) — the intermittent
    // "no avatar face" (Keith, 5 Aug 2026). The Companion also remounts the
    // embed for connection recovery, which re-armed the race on every retry.
    preloadModel: false,
    // Orthographic zoom = canvas px per world metre. 760 with the 220px
    // face pane (6 Aug 2026 taller-panel round) shows a ~29cm band —
    // slightly tighter than the previous 550/168px framing (~31cm) while
    // the face renders physically larger, per Keith's "taller panel AND
    // slightly tighter face". The minimized launcher no longer depends on
    // this zoom directly: CompanionWidget renders the embed into a fixed
    // 240px stage inner box and CSS-scales it down into the launcher
    // circle, so the whole head stays framed there too (240/760 ≈ 32cm).
    cameraZoom: 760,
    activeListen: false,
    // Explicit runtime/voice bases when the site config carries them — see
    // the CompanionSiteConfig doc comments for why the SDK's
    // platform-origin defaults no longer reach /session-stream or
    // /voice-api after the apex promo cutover.
    ...(config.runtimeUrl ? { runtimeUrl: config.runtimeUrl } : {}),
    ...(config.voiceUrl ? { voiceUrl: config.voiceUrl } : {})
  };
}
